import { useQuery } from '@tanstack/react-query';
import { HistoricalEmailReconciliation } from './historical-email-reconciliation';

interface IntakeJob {
  accountId: string;
  cacheId: number;
  stage: string;
  leaseState: 'active' | 'expired' | 'unowned';
  attempts: number;
  failed: boolean;
}

interface IntakeProgress {
  sourceUidValidity: number;
  lastContiguousUid: number;
  legacyHorizonUid: number | null;
  stalledAt: string | null;
  stallUid: number | null;
  stallErrorCode: string | null;
}

interface Overview {
  accounts: Array<{
    accountId: string; progress: IntakeProgress | null; pendingCount: number;
    historicalAmbiguousCount: number; activeLeaseCount: number;
    retryCount: number; failedCount: number;
  }>;
  intakeJobs: {
    jobCount: number; activeLeaseCount: number; retryCount: number; failedCount: number;
    truncated: boolean; items: IntakeJob[];
  };
  participantJobs: {
    pendingCount: number;
    failedCount: number;
    truncated: boolean;
    items: Array<{ pulseId: number; status: string }>;
  };
}

/** Mounted only for admins, only while setup is open. One poll for all mailboxes. */
export function EmailIntakeOverview({ accounts }: {
  accounts: Array<{ id: string; name: string }>;
}) {
  const overview = useQuery<Overview>({
    queryKey: ['/api/admin/email-intake'],
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: 1,
  });
  const data = overview.data;
  return (
    <section className="rounded border border-gray-600 p-3 space-y-2 text-sm" aria-label="Email intake health">
      <h3 className="font-semibold text-gray-100">Intake health — all active mailboxes</h3>
      {overview.isError && (
        <p role="alert" className="text-amber-400">
          Intake health unavailable{data ? ' — displayed status may be outdated' : ''}.{' '}
          <button type="button" className="underline" onClick={() => void overview.refetch()}>Retry status check</button>
        </p>
      )}
      {overview.isLoading && <p className="text-gray-400">Checking intake…</p>}
      {data && (
        <>
          {data.accounts.length === 0 && <p className="text-gray-400">No active IMAP mailboxes.</p>}
          <ul className="space-y-1">
            {data.accounts.map(account => (
              <li key={account.accountId} className={account.failedCount || account.historicalAmbiguousCount ? 'text-amber-400' : 'text-gray-300'}>
                {account.progress?.stalledAt && (
                  <p role="alert" className="text-red-400 font-semibold" data-testid={`intake-stall-${account.accountId}`}>
                    {accounts.find(item => item.id === account.accountId)?.name ?? account.accountId}: fetch stalled at UID{' '}
                    {account.progress.stallUid} ({account.progress.stallErrorCode}) since{' '}
                    {new Date(account.progress.stalledAt).toLocaleString()} — newer mail is not being taken in.
                  </p>
                )}
                <span>
                  {accounts.find(item => item.id === account.accountId)?.name ?? account.accountId}:{' '}
                  {account.pendingCount ? `${account.pendingCount} pending` : 'No pending intake'}
                </span>
                {account.pendingCount > 0 && (
                  <span>
                    {' '}— {account.activeLeaseCount} active leases, {account.retryCount} retries, {account.failedCount} failed
                    {account.historicalAmbiguousCount > 0
                      ? `; ${account.historicalAmbiguousCount} historical ambiguous (blocked)`
                      : ''}
                  </span>
                )}
                {account.progress && (
                  <span className="block text-xs text-gray-500">
                    Durable cursor at UID {account.progress.lastContiguousUid}
                    {account.progress.legacyHorizonUid ? `; pre-system history up to UID ${account.progress.legacyHorizonUid} is kept as-is` : ''}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <p className="text-gray-400">
            Historical pending records without a durable job remain blocked. Age is not used to
            guess ownership or completion. This diagnostic is read-only and never retries intake or sends email.
          </p>
          <div className={data.intakeJobs.failedCount ? 'text-red-400' : 'text-gray-300'}>
            Durable intake jobs: {data.intakeJobs.jobCount} total, {data.intakeJobs.activeLeaseCount} active leases,{' '}
            {data.intakeJobs.retryCount} retried, {data.intakeJobs.failedCount} failed.
          </div>
          {data.intakeJobs.items.length > 0 && (
            <details className="text-gray-400">
              <summary className="cursor-pointer">Inspect durable intake stages</summary>
              <ul>
                {data.intakeJobs.items.map(job => (
                  <li key={`${job.accountId}:${job.cacheId}`} className={job.failed ? 'text-red-400' : undefined}>
                    {accounts.find(item => item.id === job.accountId)?.name ?? job.accountId},
                    {' '}cache #{job.cacheId}: {job.stage}; lease {job.leaseState};
                    {' '}attempts {job.attempts}{job.failed ? '; failed' : ''}
                  </li>
                ))}
              </ul>
              {data.intakeJobs.truncated && <p>First 25 durable jobs shown.</p>}
            </details>
          )}
          <div className={data.participantJobs.failedCount ? 'text-amber-400' : 'text-gray-300'}>
            Participant processing: {data.participantJobs.pendingCount} pending, {data.participantJobs.failedCount} failed.
          </div>
          {(data.participantJobs.pendingCount + data.participantJobs.failedCount) > 0 && (
            <details className="text-gray-400">
              <summary className="cursor-pointer">Inspect participant job status</summary>
              <p>Pending jobs retry automatically. Failed jobs require investigation; this panel does not retry them or send email.</p>
              <ul>
                {data.participantJobs.items.map(job => (
                  <li key={job.pulseId}>Pulse #{job.pulseId}: {job.status}</li>
                ))}
              </ul>
              {data.participantJobs.truncated && <p>First 25 outstanding jobs shown.</p>}
            </details>
          )}
        </>
      )}
      <div className="space-y-2 border-t border-gray-700 pt-3">
        <h4 className="font-semibold text-gray-100">Historical blocked email review</h4>
        <p className="text-gray-400">
          Review is available for every configured mailbox, including inactive mailboxes.
        </p>
        {accounts.length === 0 && <p className="text-gray-400">No configured mailboxes.</p>}
        {accounts.map(account => (
          <HistoricalEmailReconciliation key={account.id} account={account} />
        ))}
      </div>
    </section>
  );
}